import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Moon, Sun, LogOut, User, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import LocalLawFinderLogo from "./LocalLawFinderLogo";

// Single source of truth for the top-nav links so desktop nav and the mobile
// drawer always show the same items. `external` is for hash anchors (Practice
// Areas) where we want a plain <a> instead of NavLink.
const NAV_LINKS = [
    { to: "/", label: "Home", testid: "nav-home", end: true },
    { to: "/find-an-attorney", label: "Find an Attorney", testid: "nav-find-attorney" },
    { to: "/#categories", label: "Practice Areas", testid: "nav-categories", external: true },
    { to: "/for-attorneys", label: "For Attorneys", testid: "nav-for-attorneys" },
    { to: "/for-attorneys/pricing", label: "Plans", testid: "nav-plans" },
    { to: "/for-affiliates", label: "Affiliates", testid: "nav-for-affiliates" },
];

export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [dark, setDark] = useState(() => {
        const saved = localStorage.getItem("ll_theme");
        if (saved) return saved === "dark";
        return false;
    });
    const [mobileOpen, setMobileOpen] = useState(false);
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const root = document.documentElement;
        if (dark) root.classList.add("dark"); else root.classList.remove("dark");
        localStorage.setItem("ll_theme", dark ? "dark" : "light");
    }, [dark]);

    // Auto-close the mobile drawer whenever the route changes (so tapping a
    // link in the drawer dismisses it). Also lock body scroll while open.
    useEffect(() => { setMobileOpen(false); }, [location.pathname, location.hash]);
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [mobileOpen]);

    // Measure the header's rendered height so the portalled mobile drawer
    // sits flush underneath it (header itself uses backdrop-blur and is the
    // containing block for fixed-positioned descendants, which is why the
    // overlay is portalled to <body>).
    useEffect(() => {
        const measure = () => {
            if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("orientationchange", measure);
        };
    }, []);

    return (
        <header ref={headerRef} className="sticky top-0 z-50 backdrop-blur bg-background/85 border-b border-border" data-testid="site-header">
            {/* Parent brand bar */}
            <div className="bg-foreground text-background text-[11px] tracking-[0.22em] uppercase">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between">
                    <span data-testid="parent-brand-label">Young Black Man Inc.</span>
                    <span className="hidden sm:inline opacity-70">Empowering Communities Through Access</span>
                </div>
            </div>

            {/* Product header */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
                <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="logo-link">
                    <LocalLawFinderLogo size={44} className="rounded-md flex-shrink-0" />
                    <div className="leading-tight min-w-0">
                        <div className="font-serif text-lg sm:text-2xl font-bold tracking-tight truncate" data-testid="product-brand">LocalLawFinder</div>
                        {/* Tagline hidden on phones to free up nav space */}
                        <div className="hidden sm:block text-[11px] sm:text-xs text-muted-foreground">Local legal answers first. Local lawyers when you need them.</div>
                    </div>
                </Link>

                {/* Desktop nav — unchanged at md+ */}
                <nav className="hidden md:flex items-center gap-7 text-sm" aria-label="Primary">
                    {NAV_LINKS.map((l) => l.external ? (
                        <a key={l.label} href={l.to} className="hover:text-primary text-foreground/80" data-testid={l.testid}>{l.label}</a>
                    ) : (
                        <NavLink key={l.label} to={l.to} end={l.end}
                                 className={({isActive}) => `hover:text-primary ${isActive ? "text-primary font-semibold" : "text-foreground/80"}`}
                                 data-testid={l.testid}>
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Right cluster — theme toggle, Sign In/dashboard, and mobile menu button */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setDark(d => !d)}
                        aria-label="Toggle theme"
                        className="h-9 w-9 rounded-full border border-border hover:bg-accent flex items-center justify-center transition-colors flex-shrink-0"
                        data-testid="theme-toggle">
                        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>

                    {!user && (
                        <Link
                            to="/attorney/auth"
                            // Always visible — Sign In is short enough to fit alongside the hamburger
                            // on iPhone 13 (390px). Also duplicated inside the mobile drawer.
                            className="inline-flex items-center rounded-full bg-primary text-primary-foreground text-sm font-semibold px-3 sm:px-4 py-2 hover:bg-primary/90 transition-colors flex-shrink-0"
                            data-testid="header-attorney-login">
                            Sign In
                        </Link>
                    )}
                    {user && (
                        <div className="flex items-center gap-2">
                            <Link
                                to={user.role === "admin" ? "/admin/dashboard" : user.role === "affiliate" ? "/affiliate/dashboard" : "/attorney/dashboard"}
                                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-accent hover:bg-accent/70 flex-shrink-0"
                                data-testid="header-dashboard-link">
                                {user.role === "admin" ? <ShieldCheck className="h-4 w-4" /> : <User className="h-4 w-4" />}
                                <span className="hidden sm:inline">{user.name}</span>
                            </Link>
                            <button
                                onClick={() => { logout(); navigate("/"); }}
                                className="h-9 w-9 rounded-full border border-border hover:bg-accent flex items-center justify-center flex-shrink-0"
                                aria-label="Sign out"
                                data-testid="header-logout">
                                <LogOut className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    {/* Hamburger — mobile only, shown <md. relative z-[60] keeps the
                     X icon clickable above the backdrop (z-40) when the drawer is open. */}
                    <button
                        type="button"
                        onClick={() => setMobileOpen((o) => !o)}
                        aria-label={mobileOpen ? "Close menu" : "Open menu"}
                        aria-expanded={mobileOpen}
                        aria-controls="mobile-nav-drawer"
                        className="md:hidden relative z-[60] h-9 w-9 rounded-full border border-border hover:bg-accent flex items-center justify-center transition-colors flex-shrink-0"
                        data-testid="mobile-menu-toggle">
                        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer — portalled to <body> so the header's backdrop-blur
            (which creates a containing block for position:fixed) doesn't trap
            the overlay. Below md only. */}
            {mobileOpen && createPortal(
                <div className="md:hidden fixed inset-0 z-[55]" data-testid="mobile-nav-overlay">
                    {/* Backdrop — tap to close, full viewport */}
                    <button
                        type="button"
                        aria-label="Close menu overlay"
                        onClick={() => setMobileOpen(false)}
                        className="absolute inset-0 w-full h-full bg-foreground/40 backdrop-blur-sm"
                        data-testid="mobile-menu-backdrop"
                    />
                    <nav
                        id="mobile-nav-drawer"
                        aria-label="Mobile primary"
                        style={{ top: headerHeight, maxHeight: `calc(100dvh - ${headerHeight}px)` }}
                        className="absolute inset-x-0 border-t border-border bg-background shadow-lg overflow-y-auto"
                        data-testid="mobile-nav-drawer">
                        <ul className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col">
                            {NAV_LINKS.map((l) => (
                                <li key={l.label}>
                                    {l.external ? (
                                        <a
                                            href={l.to}
                                            onClick={() => setMobileOpen(false)}
                                            className="block py-3 px-2 text-base text-foreground/90 hover:bg-accent/60 rounded-md"
                                            data-testid={`mobile-${l.testid}`}>
                                            {l.label}
                                        </a>
                                    ) : (
                                        <NavLink
                                            to={l.to}
                                            end={l.end}
                                            onClick={() => setMobileOpen(false)}
                                            className={({isActive}) =>
                                                `block py-3 px-2 text-base rounded-md ${isActive
                                                    ? "text-primary font-semibold bg-accent/40"
                                                    : "text-foreground/90 hover:bg-accent/60"}`}
                                            data-testid={`mobile-${l.testid}`}>
                                            {l.label}
                                        </NavLink>
                                    )}
                                </li>
                            ))}
                            {!user && (
                                <li className="mt-2 pt-3 border-t border-border">
                                    <Link
                                        to="/attorney/auth"
                                        onClick={() => setMobileOpen(false)}
                                        className="block text-center rounded-full bg-primary text-primary-foreground text-base font-semibold py-2.5 hover:bg-primary/90"
                                        data-testid="mobile-header-attorney-login">
                                        Sign In
                                    </Link>
                                </li>
                            )}
                            {user && (
                                <>
                                    <li className="mt-2 pt-3 border-t border-border">
                                        <Link
                                            to={user.role === "admin" ? "/admin/dashboard" : user.role === "affiliate" ? "/affiliate/dashboard" : "/attorney/dashboard?tab=profile"}
                                            onClick={() => setMobileOpen(false)}
                                            className="flex items-center gap-2 py-3 px-2 text-base text-foreground/90 hover:bg-accent/60 rounded-md"
                                            data-testid="mobile-nav-my-profile">
                                            {user.role === "admin" ? <ShieldCheck className="h-4 w-4" /> : <User className="h-4 w-4" />}
                                            {user.role === "attorney" ? "My profile" : user.role === "admin" ? "Admin dashboard" : "Affiliate dashboard"}
                                        </Link>
                                    </li>
                                    <li>
                                        <button
                                            type="button"
                                            onClick={() => { setMobileOpen(false); logout(); navigate("/"); }}
                                            className="w-full flex items-center gap-2 py-3 px-2 text-base text-destructive hover:bg-destructive/10 rounded-md"
                                            data-testid="mobile-nav-logout">
                                            <LogOut className="h-4 w-4" />
                                            Sign out
                                        </button>
                                    </li>
                                </>
                            )}
                        </ul>
                    </nav>
                </div>,
                document.body,
            )}
        </header>
    );
}
